import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import Actions from '../Actions';
import Search from '../Search';
import Button from '../Button';
import Icon from '../Icon';
import Table, { Hidden } from '../Table';
import Tenant from '../Tenant';
import Avatar from '../Avatar';
import Empty from '../Empty';
import Overlay from '../Overlay';
import PropertyDeveloperForm from '../PropertyDeveloperForm';
import Loader from '../Loader';
import { PATHS, ICONS } from '../../common/constants';
import { selectAuthenticatedUser } from '../../store/selectors/auth';
import { selectSearch } from '../../store/selectors/search';
import { selectLoader } from '../../store/selectors/loader';
import {
  setContactFormData,
  clearContactFormData,
} from '../../store/actions/contactForm';
import { fetchPropertyDeveloperDetails } from '../../store/actions/properties';
import { selectContactFormData } from '../../store/selectors/contactForm';

import { variables } from '../../Tenants';
import { checkSparkDataAvailable } from '../../helpers/validationHelper';
import { onRequestPhoneCall } from '../../helpers/validationHelper';

class PropertyDevelopersTable extends Component {
  // Class property that initializes component state (functional component equivalent: const [showOverlay, setShowOverlay] = useState(false)).
  state = {
    showOverlay: false,
  };

  /* Lifecycle method that runs after this component mounts to the DOM. Used to initialize the contact form data state.
   * Functional component equivalent to this with hooks would be: useEffect(() => { ... }, []); */
  componentDidMount() {
    const { setContactFormData } = this.props;
    setContactFormData({ showOverlay: false });
  }

  // Render method which renders the JSX from this component back to the DOM.
  render() {
    const {
      user,
      propertyDevelopers,
      onSearch,
      search,
      setContactFormData,
      clearContactFormData,
      onSubmitContactForm,
      sendActivationLinkToUser,
      sendResetPasswordLinkToUser,
      onDeleteContact,
      onFetchPropertyManagerDetails,
      contactFormData,
      loader,
      propertyData,
    } = this.props;

    // Column definitions for the property developer table with formatting, sorting, and action handlers.
    const developerTableConfig = [
      {
        label: 'Contacts',
        sortable: true,
        accessor: 'firstname',
        cell: (data) => {
          return (
            <Link to={PATHS.CONTACTS_VIEW.replace(':id', data.id)}>
              <Tenant
                headline={`${data.firstname} ${data.lastname}`}
                avatar={
                  <Avatar
                    badge={ICONS['Property Developer']}
                    url={data.profileImage}
                  />
                }
              />
            </Link>
          );
        },
      },
      {
        xsHide: true,
        label: 'Role',
        cell: (data) => (
          <div style={{ display: 'flex' }}>
            {data.role === 'Property Developer'
              ? variables.propertyDeveloper
              : data.role}
            {data.isPrimaryContact === true ? (
              <div
                style={{
                  display: 'flex',
                  color: '#48C2BF',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingLeft: 5,
                }}
              >
                <Icon glyph="sun" weight="regular" size="small" />
              </div>
            ) : (
              <div />
            )}
          </div>
        ),
      },
      {
        label: 'Phone',
        accessor: 'phone',
        cell: (data) => onRequestPhoneCall(data.phone),
      },
      {
        xsHide: true,
        label: 'Email',
        accessor: 'email',
      },
      {
        cell: (data) => (
          <Hidden>
            {checkSparkDataAvailable(
              propertyData,
              user.roles[0].role.name
            ) ? null : (
              <Button
                color="semantic"
                label={<Icon glyph="pencil" weight="light" />}
                onClick={() => {
                  onFetchPropertyManagerDetails(data.id, user.id);
                  setContactFormData({
                    isPrimaryPropertyDeveloperContact: data.isPrimaryContact,
                    formType: 'New',
                  });
                }}
              />
            )}
            {user.roles[0].role.name === 'Admin' ||
            user.roles[0].role.name === 'Super Admin' ||
            user.roles[0].role.name === 'Property Developer' ? (
              <Button
                color="semantic"
                onClick={(e) =>
                  data.isPrimaryContact === false
                    ? window.confirm(
                        `Are you sure you want to remove ${data.firstname} ${data.lastname} from this project?`
                      ) && onDeleteContact(data)
                    : alert(
                        'Please select other property developer as Primary property developer before deleting this property developer'
                      )
                }
                label={<Icon glyph="trash" weight="light" />}
              />
            ) : null}
          </Hidden>
        ),
      },
    ];

    return (
      <>
        {loader ? <Loader center={true} /> : null}
        <Actions compact>
          <Search
            size="small"
            placeholder={'Search People...'}
            onChange={onSearch}
          />
          {user.roles[0].role.name === 'Admin' ||
          user.roles[0].role.name === 'Super Admin' ||
          user.roles[0].role.name === 'Property Developer' ? (
            checkSparkDataAvailable(
              propertyData,
              user.roles[0].role.name
            ) ? null : (
              <Button
                color="teal"
                label={'Add Contact'}
                onClick={() => {
                  this.setState({ showOverlay: true });
                  clearContactFormData();
                  setContactFormData({
                    firstname: '',
                    lastname: '',
                    email: '',
                    phone: '',
                    type: 'default',
                    showOverlay: true,
                    isPrimaryContact: false,
                    formType: 'New',
                  });
                }}
                icon={<Icon glyph="user-plus" weight="regular" size="small" />}
                size="small"
              />
            )
          ) : null}
        </Actions>
        {propertyDevelopers && propertyDevelopers.length ? (
          <>
            <Table columns={developerTableConfig} data={propertyDevelopers} />
          </>
        ) : (
          <Empty
            direction="ttb"
            headline={'No people yet'}
            strapline={
              'Keep all your contacts here so it’s easier for you to manage.'
            }
            searchIsActive={search && search.contacts ? search.contacts : ''}
            cta={
              checkSparkDataAvailable(
                propertyData,
                user.roles[0].role.name
              ) ? null : (
                <>
                  <Button
                    color="teal"
                    label={'Add Person'}
                    onClick={() => {
                      this.setState({ showOverlay: true });
                      clearContactFormData();
                      setContactFormData({
                        firstname: '',
                        lastname: '',
                        email: '',
                        phone: '',
                        type: 'default',
                        showOverlay: true,
                      });
                    }}
                    icon={<Icon glyph="user" weight="light" size="small" />}
                    size="small"
                  />
                </>
              )
            }
            image={<img alt="No people yet" src="/assets/empty/contacts.svg" />}
          />
        )}
        <Overlay
          showOverlay={
            contactFormData.showOverlay && contactFormData.showOverlay === true
              ? contactFormData.showOverlay &&
                contactFormData.showOverlay === true
              : false
          }
          content={
            <PropertyDeveloperForm
              onClose={() =>
                this.setState({ showOverlay: false }, () =>
                  clearContactFormData()
                )
              }
              onSubmit={(data, actionType) => {
                onSubmitContactForm(data, actionType);
                clearContactFormData();
              }}
              sendResetPasswordLinkToUser={sendResetPasswordLinkToUser}
              sendActivationLinkToUser={sendActivationLinkToUser}
            />
          }
        />
      </>
    );
  }
}

/* Selects specific pieces of Redux state and maps them to component props
 * This function receives the entire Redux state and returns an object of props
 * Each selector (selectAuthenticatedUser, etc.) extracts specific data from the store
 * Modern approach with hooks would be:
 * const user = useSelector(selectAuthenticatedUser)
 * const search = useSelector(selectSearch) etc., with each piece of state accessed individually. */
const mapStateToProps = (state) => ({
  user: selectAuthenticatedUser(state),
  search: selectSearch(state),
  loader: selectLoader(state),
  contactFormData: selectContactFormData(state),
});

/* Maps Redux action creators to component props.
 * bindActionCreators wraps each function with dispatch() so component can call them directly.
 * e.g., this.props.setContactFormData(data) will dispatch the action to Redux
 * Hooks equivalent:
 * const dispatch = useDispatch();
 * const handleAction = (data) => dispatch(setContactFormData(data)); */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setContactFormData: setContactFormData,
      clearContactFormData: clearContactFormData,
      onFetchPropertyManagerDetails: fetchPropertyDeveloperDetails,
    },
    dispatch
  );

/* connect() is a Higher-Order Component (HOC) pattern from Redux that:
 * 1. Creates a wrapper component that subscribes to the Redux store.
 * 2. Injects state as props using 'mapStateToProps' when the store changes.
 * 3. Provides functions as props (via 'mapDispatchToProps') that, when called, will dispatch actions to Redux.
 * 4. Re-renders the wrapped component only when its specific slice of state changes in the store.
 * 5. Handles performance optimization with shallow comparison of mapped props. The flow works like:
 * Redux Store → connect wrapper → mapStateToProps/mapDispatchToProps → PropertyDevelopersTable. */
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PropertyDevelopersTable);
